import { FormProvider } from '@neo/blocks/forms/form-provider'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import type React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import type { Credential } from 'v2/types/api/settings'
import { generateToastsForMutation } from 'v2/utils/toasts'
import {
  useCreateCredentialMutation,
  useDeleteCredentialMutation,
  useUpdateCredentialMutation,
} from '../queries'
import { type CredentialFormValues, credentialFormFields } from './credential-modal.config'

interface CredentialModalProps {
  isOpen: boolean
  onClose: () => void
  editData?: Credential | null
}

export const CredentialModal: React.FC<CredentialModalProps> = ({ isOpen, onClose, editData }) => {
  const isEditing = !!editData
  const [isPasswordEnabled, setIsPasswordEnabled] = useState(false)

  const createMutation = useCreateCredentialMutation()
  const updateMutation = useUpdateCredentialMutation()
  const deleteMutation = useDeleteCredentialMutation()

  const handleSubmit = (values: CredentialFormValues) => {
    if (isEditing && editData) {
      const updatePayload: Partial<CredentialFormValues> = {
        name: values.name,
        username: values.username,
      }
      if (values.password) {
        updatePayload.password = values.password
      }

      updateMutation.mutate(
        {
          credential_uuid: editData.credential_uuid,
          ...updatePayload,
        },
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Credential updated successfully',
            callback: () => {
              setIsPasswordEnabled(false)
              onClose()
            },
          },
          {
            title: 'Error',
            description: 'Failed to update credential',
          },
        ),
      )
    } else {
      createMutation.mutate(
        values,
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Credential created successfully',
            callback: () => {
              setIsPasswordEnabled(false)
              onClose()
            },
          },
          {
            title: 'Error',
            description: 'Failed to create credential',
          },
        ),
      )
    }
  }

  const handleDelete = () => {
    if (editData) {
      deleteMutation.mutate(
        editData.credential_uuid,
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Credential deleted successfully',
            callback: () => {
              setIsPasswordEnabled(false)
              onClose()
            },
          },
          {
            title: 'Error',
            description: 'Failed to delete credential',
          },
        ),
      )
    }
  }

  const handlePasswordChangeClick = useCallback(() => {
    setIsPasswordEnabled(true)
  }, [])

  const defaultValues: CredentialFormValues = useMemo(
    () => ({
      name: editData?.name || '',
      username: editData?.username || '',
      password: '',
    }),
    [editData],
  )

  const fields = useMemo(
    () => credentialFormFields(isEditing, isPasswordEnabled, handlePasswordChangeClick),
    [isEditing, isPasswordEnabled, handlePasswordChangeClick],
  )

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setIsPasswordEnabled(false)
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit credential' : 'Create new credential'}</DialogTitle>
        </DialogHeader>

        <FormProvider>
          <GenericForm<CredentialFormValues>
            fields={fields}
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            className="grid grid-cols-2 gap-4"
            id="credential-form"
            includeSubmitButton={false}
          />
          <DialogFooter className="sm:flex-row sm:justify-between sm:space-x-2">
            {isEditing && (
              <Button
                type="button"
                variant="destructive-outline"
                onClick={handleDelete}
                data-testid="button-delete-credential"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
            <FormSubmitButton
              formId="credential-form"
              loading={createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-credential"
            >
              {isEditing ? 'Update credential' : 'Create credential'}
            </FormSubmitButton>
          </DialogFooter>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
